import { Group, GroupFilterCondition } from "@fairfleet/geotab";
import { createContext, useCallback, useContext, useState } from "react";
import { AddinState } from "@/utilities/models/AddinState";

interface AddInStateContextProps {
  groups?: Group[];
  updateGroups: (groups: Group[]) => void;
  groupFilterCondition?: GroupFilterCondition;
  state?: AddinState;
}

const AddInStateContext = createContext<AddInStateContextProps | undefined>(undefined);

const stateToContext = (state?: AddinState) => {
  const groups =
    state && "getGroupFilter" in state && typeof state.getGroupFilter === "function"
      ? state.getGroupFilter()
      : undefined;

  const groupFilterCondition =
    state && "getAdvancedGroupFilter" in state && typeof state.getAdvancedGroupFilter === "function"
      ? state.getAdvancedGroupFilter()
      : undefined;

  return { groups, groupFilterCondition };
};

export const AddInStateProvider: React.FC<{ children: React.ReactNode; state?: AddinState }> = ({
  children,
  state,
}) => {
  const { groups: stateGroups, groupFilterCondition: stateGroupFilterCondition } =
    stateToContext(state);
  const [groups, setGroups] = useState<Group[] | undefined>(stateGroups);
  // eslint-disable-next-line unused-imports/no-unused-vars
  const [groupFilterCondition, setGroupFilterCondition] = useState<
    GroupFilterCondition | undefined
  >(stateGroupFilterCondition);

  const updateGroups = useCallback((newGroups: Group[]) => {
    setGroups(newGroups?.length > 0 ? newGroups : undefined);
  }, []);

  return (
    <AddInStateContext.Provider
      value={{
        groups,
        updateGroups,
        groupFilterCondition,
        state,
      }}
    >
      {children}
    </AddInStateContext.Provider>
  );
};

export const useAddInState = () => {
  const context = useContext(AddInStateContext);
  if (!context) {
    throw new Error("useAddInContext must be used within a AddInProvider");
  }
  return context;
};
